import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import CSVUploadZone from '../../../../components/import/csv-upload-zone';
import CSVValidationResults from '../../../../components/import/csv-validation-results';
import CSVImportSuccess from '../../../../components/import/csv-import-success';
import { assetImportService } from '../../../../services/asset-import.service';
import { ImportStatus } from '../../../../types/asset-import.types';
import type { CSVValidationResult } from '../../../../types/asset-import.types';

export default function CSVImport() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<ImportStatus>(ImportStatus.Idle);
  const [validationResult, setValidationResult] = useState<CSVValidationResult | null>(null);
  const [importedCount, setImportedCount] = useState(0);
  const [uploadProgress, setUploadProgress] = useState(0);

  const handleFileUpload = async (file: File) => {
    try {
      setStatus(ImportStatus.Uploading);
      setUploadProgress(0);

      // Simulate upload progress
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => Math.min(prev + 10, 90));
      }, 200);

      const result = await assetImportService.uploadCSV(file);

      clearInterval(progressInterval);
      setUploadProgress(100);

      setValidationResult(result);
      setStatus(ImportStatus.Validating);
    } catch (error) {
      console.error('Upload failed:', error);
      setStatus(ImportStatus.Error);
    }
  };

  const handleImport = async (assetIds: string[]) => {
    try {
      setStatus(ImportStatus.Processing);
      const result = await assetImportService.importValidAssets(assetIds);

      if (result.success) {
        setImportedCount(result.importedCount);
        setStatus(ImportStatus.Complete);
      } else {
        setStatus(ImportStatus.Error);
      }
    } catch (error) {
      console.error('Import failed:', error);
      setStatus(ImportStatus.Error);
    }
  };

  const handleReset = () => {
    setStatus(ImportStatus.Idle);
    setValidationResult(null);
    setImportedCount(0);
    setUploadProgress(0);
  };

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="border-b bg-card">
        <div className="px-6 py-4">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/assets/import')}
              className="h-8 w-8"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="flex-1">
              <h1 className="text-2xl font-bold tracking-tight">CSV Import</h1>
              <p className="text-sm text-muted-foreground mt-1">
                Upload a CSV file containing your asset data
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto">
        <div className="container mx-auto p-6 space-y-6">
          {/* Content based on status */}
          {status === ImportStatus.Idle && (
            <CSVUploadZone onFileUpload={handleFileUpload} />
          )}

          {(status === ImportStatus.Uploading || status === ImportStatus.Processing) && (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
              <p className="text-lg font-medium">
                {status === ImportStatus.Uploading ? 'Uploading and validating...' : 'Importing assets...'}
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                {uploadProgress}% complete
              </p>
            </div>
          )}

          {status === ImportStatus.Validating && validationResult && (
            <CSVValidationResults
              result={validationResult}
              onImport={handleImport}
              onCancel={handleReset}
            />
          )}

          {status === ImportStatus.Complete && (
            <CSVImportSuccess
              count={importedCount}
              onViewAssets={() => navigate('/assets')}
              onImportMore={handleReset}
            />
          )}

          {status === ImportStatus.Error && (
            <div className="text-center py-12">
              <p className="text-lg font-medium text-destructive">
                An error occurred during import
              </p>
              <Button onClick={handleReset} className="mt-4">
                Try Again
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
